import { useCallback, useRef, useState } from "react";
import moment from "moment";
import axiosInstance from "../utils/axiosConfig";

// Si el rango abarca más meses que esto se hace una sola consulta sin filtros
// en vez de una por mes.
const MAX_MONTH_REQUESTS = 12;
// Peticiones simultáneas al pedir el detalle completo de cada servicio.
const DETAIL_CONCURRENCY = 6;

const buildMonthParams = (from, to) => {
  const start = moment(from, "YYYY-MM-DD").startOf("month");
  const end = moment(to, "YYYY-MM-DD").startOf("month");

  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return [];

  const months = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end) && months.length <= MAX_MONTH_REQUESTS) {
    months.push({
      month: cursor.clone().locale("en").format("MMMM"),
      year: cursor.year(),
    });
    cursor.add(1, "month");
  }

  return months.length > MAX_MONTH_REQUESTS ? [] : months;
};

const dedupeById = (schedules) => {
  const byId = new Map();
  schedules.forEach((schedule) => {
    if (schedule && schedule.id !== undefined) byId.set(schedule.id, schedule);
  });
  return [...byId.values()];
};

/** Ejecuta las promesas en tandas para no saturar el backend. */
const runWithConcurrency = async (items, worker, onProgress) => {
  const results = [];
  let index = 0;
  let done = 0;

  const runners = Array.from(
    { length: Math.min(DETAIL_CONCURRENCY, items.length) },
    async () => {
      while (index < items.length) {
        const current = index++;
        results[current] = await worker(items[current]);
        done += 1;
        onProgress?.(done, items.length);
      }
    }
  );

  await Promise.all(runners);
  return results;
};

export const useServiceReports = () => {
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailProgress, setDetailProgress] = useState(null);
  const detailsCache = useRef(new Map());

  /** Trae los servicios del rango indicado (fechas en formato YYYY-MM-DD). */
  const fetchSchedules = useCallback(async ({ from, to }) => {
    setIsLoading(true);
    setError(null);

    try {
      const monthParams = buildMonthParams(from, to);

      const responses = monthParams.length
        ? await Promise.all(
            monthParams.map((params) =>
              axiosInstance.get("/schedules/light", { params })
            )
          )
        : [await axiosInstance.get("/schedules/light")];

      const rangeStart = moment(from, "YYYY-MM-DD").startOf("day");
      const rangeEnd = moment(to, "YYYY-MM-DD").endOf("day");

      const data = dedupeById(
        responses.flatMap((response) =>
          Array.isArray(response.data) ? response.data : []
        )
      )
        .filter((schedule) => {
          const start = moment(schedule.start_time);
          if (!start.isValid()) return false;
          if (rangeStart.isValid() && start.isBefore(rangeStart)) return false;
          if (rangeEnd.isValid() && start.isAfter(rangeEnd)) return false;
          return true;
        })
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

      setSchedules(data);
      return data;
    } catch (err) {
      console.error("Error al cargar el informe de servicios", err);
      setError(err);
      setSchedules([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Completa los servicios indicados con su detalle (`GET /schedules/:id`),
   * que es donde vienen sede, edificio, ascensor y las notas del checklist.
   */
  const fetchDetails = useCallback(async (items) => {
    const pending = items.filter((item) => !detailsCache.current.has(item.id));

    if (pending.length) {
      setDetailProgress({ loaded: 0, total: pending.length });

      await runWithConcurrency(
        pending,
        async (item) => {
          try {
            const { data } = await axiosInstance.get(`/schedules/${item.id}`);
            detailsCache.current.set(item.id, data);
          } catch (err) {
            console.error(`Error al cargar el detalle del servicio ${item.id}`, err);
            detailsCache.current.set(item.id, null);
          }
        },
        (loaded, total) => setDetailProgress({ loaded, total })
      );

      setDetailProgress(null);
    }

    return items.map((item) => ({
      ...item,
      ...(detailsCache.current.get(item.id) || {}),
    }));
  }, []);

  return {
    schedules,
    isLoading,
    error,
    detailProgress,
    fetchSchedules,
    fetchDetails,
  };
};

export default useServiceReports;
